import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {filter, finalize, takeUntil} from 'rxjs/operators';
import {TypedForm, TypedFormOwnControls} from '../../common/form-types';
import {EMAIL_VALIDATION_PATTERN} from '../../common/form-utils';
import {AuthService} from '../../components/auth/auth.service';
import {destroy$} from '../../components/destroyable';
import {AppConfigService} from '../../services/config.service';
import {SignUpService, UserSignUpRequest} from '../../services/sign-up.service';
import {SignUpError} from './sign-up-error';

interface SignUpFormFields {
    username: string;
    fullName: string;
    email: string;
    password: string;
    repeatPassword: string;
    referralId: string;
}

type SignUpFormControls = TypedFormOwnControls<SignUpFormFields>;

type SignUpForm = TypedForm<SignUpFormFields>;

const FORM_ERROR_DIFFERENT_PASSWORDS = 'notSame';

const ERROR_EMPTY_FIELDS = 'components.sign-up-page.error_empty_fields';
const ERROR_PASSWORD_MISMATCH = 'components.sign-up-page.error_password_mismatch';
const ERROR_USER_ID_EXISTS = 'components.sign-up-page.error_user_id_exists';
const ERROR_EMAIL_EXISTS = 'components.sign-up-page.error_email_exists';
const ERROR_EMAIL_NOT_VALID = 'components.sign-up-page.error_email_not_valid';

enum SignUpPageState {
    FORM         = 'FORM',
    CONFIRMATION = 'CONFIRMATION',
    SUCCESS      = 'SUCCESS'
}

@Component({
    templateUrl: './sign-up-page.component.html',
    styleUrls: ['./sign-up-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SignUpPageComponent implements OnInit {

    public form: SignUpForm;
    public submitting: boolean = false;
    public errorMessage: string = '';
    public userIdErrorMessage: string = '';
    public emailErrorMessage: string = '';

    public pageTitle: string = 'components.sign-up-page.create_new_user';
    public replacementData: {email: string} = {
        email: ''
    };

    public state: SignUpPageState = SignUpPageState.FORM;

    public readonly SignUpPageState = SignUpPageState;

    public isLoading: boolean = false;

    public readonly useEmailAsUsername: boolean = this.appConfigService.appConfig.signUpEmailAsUsername || false;

    public readonly signUpDisplayReferralIdInput: boolean =
                        this.appConfigService.appConfig.signUpDisplayReferralIdInput || false;

    public readonly destroy$ = destroy$();

    private readonly isInstantAccess: boolean = this.appConfigService.appConfig.signUpInstantAccess || false;

    constructor(
        private signUpService: SignUpService,
        private appConfigService: AppConfigService,
        private activatedRoute: ActivatedRoute,
        private authService: AuthService
    ) {}

    public ngOnInit(): void {
        this.buildForm();
    }

    public submit(): void {
        const controls = this.form.controls;
        this.errorMessage = '';
        this.userIdErrorMessage = '';
        this.emailErrorMessage = '';

        if (this.form.valid && !this.isLoading) {
            this.sendSignUpRequest();
        } else if (this.form.hasError(FORM_ERROR_DIFFERENT_PASSWORDS)) {
            this.errorMessage = ERROR_PASSWORD_MISMATCH;
        } else if (controls.email.errors && controls.email.errors['pattern']) {
            this.emailErrorMessage = ERROR_EMAIL_NOT_VALID;
        } else {
            this.errorMessage = ERROR_EMPTY_FIELDS;
        }
    }

    public back(): void {
        this.state = SignUpPageState.FORM;
    }

    private buildForm(): void {
        const usernameValidators: ValidatorFn[] = this.useEmailAsUsername
            ? null
            : [Validators.required];
        const controls: SignUpFormControls = {
            username:       new FormControl('', usernameValidators),
            fullName:       new FormControl('', [Validators.required]),
            email:          new FormControl('', [
                Validators.required,
                Validators.pattern(EMAIL_VALIDATION_PATTERN)
            ]),
            password:       new FormControl('', [Validators.required]),
            repeatPassword: new FormControl(''),
            referralId:     new FormControl('')
        };

        this.form = new FormGroup(controls, {validators: this.checkPassword as unknown as ValidatorFn}) as SignUpForm;

        this.activatedRoute.queryParamMap.pipe(
            filter(paramMap => paramMap.has('referral-id')),
            takeUntil(this.destroy$)
        ).subscribe(paramMap => this.form.controls.referralId.setValue(paramMap.get('referral-id')));
    }

    private checkPassword(group: SignUpForm): null | {[FORM_ERROR_DIFFERENT_PASSWORDS]: boolean} {
        const pass = group.controls.password.value;
        const confirmPass = group.controls.repeatPassword.value;

        return pass === confirmPass ? null : {[FORM_ERROR_DIFFERENT_PASSWORDS]: true};
    }

    private sendSignUpRequest(): void {
        this.isLoading = true;

        const form: SignUpFormFields = this.form.value;
        const email = form.email.trim();
        const login = this.useEmailAsUsername
            ? email
            : form.username.trim();
        const passwordHash = this.authService.encodePassword(form.password);

        const userRequest: UserSignUpRequest = {
            email,
            login,
            fullName:   form.fullName.trim(),
            password:   passwordHash,
            referralId: form.referralId.trim()
        };

        this.signUpService.createUser(userRequest).pipe(
            finalize(() => this.isLoading = false),
            takeUntil(this.destroy$)
        ).subscribe(
            () => this.setPageInformationMode(),
            error => {
                if (error && error.error) {
                    this.showSignUpErrors(error.error);
                }
            }
        );
    }

    private setPageInformationMode(): void {
        this.replacementData.email = this.form.controls.email.value;

        if (this.isInstantAccess) {
            this.pageTitle = 'components.sign-up-page.sign_up';
            this.state = SignUpPageState.SUCCESS;
        } else {
            this.pageTitle = 'components.sign-up-page.confirmation_link';
            this.state = SignUpPageState.CONFIRMATION;
        }
    }

    private showSignUpErrors(errors: SignUpError[] | unknown): void {
        if ((errors as SignUpError[]).length) {
            (errors as SignUpError[]).forEach((errorCode: SignUpError | unknown[]) => {
                switch (errorCode) {

                    case SignUpError.EMAIL_EXISTS:
                        this.emailErrorMessage = ERROR_EMAIL_EXISTS;
                        break;

                    case SignUpError.LOGIN_EXISTS:
                        this.userIdErrorMessage = ERROR_USER_ID_EXISTS;
                        break;
                }
            });
        }
    }

}
